import _ from 'lodash';
import Immutable from 'immutable';
import { EventEmitter } from 'events';
import assign from 'object-assign';
import AppDispatcher from '../dispatcher/AppDispatcher';
import Constants from '../constants/Constants';
import MessageStore from './MessageStore.js';
import MemberStore from './MemberStore.js';
import Updates from '../../app/updates.js';

const ActionTypes = Constants.ActionTypes;
const CHANGE_EVENT = 'change';

const Conversation = Immutable.Record({
  id: undefined, // Sarakstes ID, sakrīt ar lietotāja ID
  last_message_id: undefined, // Pēdējās vēstules id
  message: undefined, // Vēstules objekts, iegūst no MessageStore
  unread: false, // Vai izlasīta?
  deleted: false,
  updated: undefined, // Kad pēdējo reizi atjaunota
});

let _currentID = null; // vienāds ar member_id
let _conversations = Immutable.OrderedMap(); // sarakstes id => Conversation Record
let _conversationsIds = []; // Visu sarakstu IDs (member ids)
const _checked = {}; // atzīmētās sarakstes
let _lastReceivedCount = null; // pēdējo ielādēto sarakstu skaits - papildina loadMore gadījumā
let _unread = 0; // neizlasītās sarakstes
// mainās vērtība uz TRUE tiklīdz izvēlās pirmo saraksti
// kamēr nav TRUE, netiek uzsetota aktīvā sarakste
let _canSelectConversation = false;
// nospiesta load more poga
let _loadMore = false;
// cik sarakstes ielādētas atverot vēstuļu sadaļu
let _openConversationsCount = false;
let _conversationsEmpty = false;

const ConversationStore = assign({}, EventEmitter.prototype, {

  emitChange() {
    this.emit(CHANGE_EVENT);
  },

  /**
	* @param {function} callback
	*/
  addChangeListener(callback) {
    this.on(CHANGE_EVENT, callback);
  },

  /**
	* @param {function} callback
	*/
  removeChangeListener(callback) {
    this.removeListener(CHANGE_EVENT, callback);
  },

  /**
	* $Set
	*/

  /**
	* Pievieno atzīmēto saraksti
	*/
  setChecked(conversationID) {
    // pārbauda vai ir saraktes un vai konkrētā eksistē
    if (!_conversationsIds.length || !this.get(conversationID)) {
      return false;
    }

    _checked[conversationID] = true;

    return true;
  },

  // uzstāda pēdējo ielādēto sarakstu skaitu - papildina loadMore gadījumā
  setLastReceivedCount(count) {
    _lastReceivedCount = count;
  },

  /**
	* Norāda aktuālo saraksti
	*
	* @param {string} id
	* @param {bool} allowNull
	*/
  setCurrentId(id, allowNull = false) {
    if (!_canSelectConversation) {
      return false;
    }

    if (id || (id == null && allowNull)) {
      _currentID = id;
      return true;
    }

    // ja nav uzsetots, tad aktuālā ir pirmā no sakārtotā saraksta
    if (!_currentID && _conversations.size) {
      _currentID = _conversations.first().get('id');
      return true;
    }

    return false;
  },

  /**
	* Norāda, ka neviena nav aktīvā sarakste
	*/
  setEmptyCurrentId() {
    _currentID = null;
    return true;
  },

  /**
	* Uzseto pazīmi, ka sarakste izlasīta, apskatīta
	*/
  setRead(conversationID) {
    const conversationTmp = _conversations.get(conversationID);

    if (!conversationTmp) {
      return false;
    }

    _conversations = _conversations.update(conversationID, item => item.set('unread', false));
  },

  /**
	* Uzseto Skaitu - neizlasītās sarakstes
	*/
  setUnread(nr) {
    if (typeof nr === 'undefined' || nr < 0) {
      return;
    }

    _unread = nr;

    // atjauno augšējā izvēlnē
    Updates.updateConversationsCount(nr);
  },

  /**
	* Norāda pazīmi, vai drīkst izvēlēties saraksti
	*/
  setCanSelectConversation(status) {
    _canSelectConversation = status;
  },

  /**
	* Vai nospiesta load more poga
	*/
  setLoadMore(value) {
    _loadMore = value;
  },

  /**
	* Vai sarakstēs ir tukšums
	*/
  setConversationsEmpty(value) {
    _conversationsEmpty = value;
  },

  /**
	* Cik sarakstes ielādētas atverot vēstuļu sadaļu
	*/
  setOpenConversationsCount(value) {
    _openConversationsCount = value;
  },

  /**
	* $Get
	*/

  /**
	* @param {string} id
	*/
  get(id) {
    return _conversations.get(id);
  },

  getAll() {
    return _conversations;
  },

  getCurrentID() {
    return _currentID;
  },

  getCurrent() {
    return this.get(this.getCurrentID());
  },

  getLast() {
    return _conversations.last();
  },

  getFirst() {
    return _conversations.first();
  },

  getTotal() {
    return _conversations.size;
  },

  // atgriež pēdējo ielādēto sarakstu skaitu - papildina loadMore gadījumā
  getLastReceivedCount() {
    return _lastReceivedCount;
  },

  /**
	* Atzīmētās
	*/
  getChecked() {
    return _checked;
  },

  /**
	* Kopējais atzīmēto sarakstu skaits
	*/
  getCheckedTotal() {
    return _.size(_checked);
  },

  /**
	* Iegūst skaitu - neizlasītās sarakstes
	*/
  getUnread() {
    return _unread;
  },

  /**
	* Vai nospiesta load more poga
	*/
  getLoadMore() {
    return _loadMore;
  },

  /**
	* Vai sarakstēs ir tukšums
	*/
  getConversationsEmpty() {
    return _conversationsEmpty;
  },

  /**
	* Cik sarakstes ielādētas atverot vēstuļu sadaļu
	*/
  getOpenConversationsCount(value) {
    return _openConversationsCount;
  },

  /**
	* $Add
	*/

  /**
	* Pievieno saraksti
	*/
  addConversation(conversation) {
    const conversationID = conversation.id;

    // pārbauda vai šāda sarakste eksistē
    if (!_conversations.has(conversationID)) {
      // izveido jaunu ierakstu
      _conversations = _conversations.set(conversationID, new Conversation(conversation));
      // pievieno jauno id
      _conversationsIds[_conversationsIds.length] = conversationID;
      return;
    }

    const conversationTmp = _conversations.get(conversationID);

    // salīdzina pēc updated lauka, vai ir kaut kas jauns sarakstē
    if (conversationTmp.get('updated') < conversation.updated) {
      _conversations = _conversations.update(conversationID, updateConversationItem => updateConversationItem.withMutations((updateConversation) => {
        updateConversation
          .set('updated', conversation.updated)
          .set('unread', conversation.unread)
          .set('deleted', conversation.deleted)
          .set('last_message_id', conversation.last_message_id);
      }));
    }
  },

  addConversations(conversations) {
    if (_.isEmpty(conversations)) {
      return false;
    }

    conversations.forEach(function (conversation) {
      this.addConversation(conversation);
    }, this);

    this.sortConversations();
    this.setCurrentId();
  },

  /**
	* Pievieno vēstules katrai sarakstei, lai ir info par pēdējo vēstuli
	*/
  addMessages(conversationsIds) {
    if (_.isEmpty(conversationsIds)) {
      return false;
    }

    const self = this;
    // iegūst pēdējo vēstuli katrai sarakstei
    const messages = MessageStore.getLastMessagesByMembers(conversationsIds);

    conversationsIds.forEach((memberId) => {
      // pārbauda vai ir konkrētā sarakste
      if (!self.get(memberId)) {
        return false;
      }

      let message = null;
      if (messages[memberId]) {
        message = messages[memberId];
      }

      // pievieno vēstuli sarakstei
      _conversations = _conversations.update(memberId, item => item.set('message', message));
    });
  },

  /**
  * $Update
  */

  /**
  * Atjauno sarakstes pēdējo vēstuli
  */
  updateConversationLastMessage(id) {
    if (!id || !_conversationsIds.includes(id)) {
      return false;
    }

    const lastMessage = MessageStore.getLastMessage(id);
    if (!lastMessage) {
      return false;
    }

    _conversations = _conversations.update(id, item => item.set('message', lastMessage));
    return true;
  },

  /**
	* $States
	*/

  /**
	* Iegūst info, vai ir atzīmēta sarakste
	*/
  isChecked(conversationID) {
    if (!_checked[conversationID]) {
      return false;
    }

    return _checked[conversationID];
  },

  /**
	* $Delete
	*/

  /**
	* Noņem pazīmi ka atzīmēta sarakste
	*/
  removeChecked(conversationID) {
    if (typeof _checked[conversationID] === 'undefined') {
      return false;
    }

    delete _checked[conversationID];

    return true;
  },

  // dzēst saraksti
  deleteConversation(conversationID) {
    // atvertā sarakste
    if (_currentID == conversationID) {
      _currentID = null;
    }

    // dzēst no _conversationsIds
    _conversationsIds = _.reject(_conversationsIds, id => conversationID === id);

    // dzēst no _conversations
    _conversations = _conversations.delete(conversationID);

    // dzēst no atzīmētajām
    delete _checked[conversationID];
  },

  /**
	* $Sort
	*/

  /**
	* Sakārto sarakstes
	*/
  sortConversations() {
    _conversations =
			_conversations.sortBy(conversation => conversation.get('updated'), (a, b) => b - a);
  },

});

ConversationStore.dispatchToken = AppDispatcher.register((action) => {
  switch (action.type) {
    case ActionTypes.CONVERSATIONS_LOAD:
      if (action.loadMoreClick) {
        ConversationStore.setLoadMore(true);
        ConversationStore.emitChange();
      }
      break;

    case ActionTypes.CONVERSATIONS_LOAD_SUCCESS:
      AppDispatcher.waitFor([MemberStore.dispatchToken]);
      var count = action.conversations.length;
      var unread_conversations = action.unread_conversations;
      var doEmitChange = false;
      // cik vēstules atvērtas pie pirmās sarakstu ielādes
      if (ConversationStore.getOpenConversationsCount() === false) {
        ConversationStore.setOpenConversationsCount(count);
      }

      // load more gadījumā pievieno vēstuļu skaitu, cik ielādētas
      if (action.loadMoreClick) {
        ConversationStore.setLastReceivedCount(count);
        ConversationStore.setLoadMore(false);
        doEmitChange = true;
      }

      if (count > 0) {
        ConversationStore.addConversations(action.conversations);
        if (ConversationStore.getConversationsEmpty()) {
          ConversationStore.setConversationsEmpty(false);
        }
        doEmitChange = true;
      } else {
        const totalConv = ConversationStore.getTotal();
        const isEmpty = ConversationStore.getConversationsEmpty();

        if (totalConv == 0 && !isEmpty) {
          ConversationStore.setConversationsEmpty(true);
          doEmitChange = true;
        } else if (totalConv > 0 && isEmpty) {
          ConversationStore.setConversationsEmpty(false);
          doEmitChange = true;
        }
      }

      // pievieno skaitu ar neizlasītām sarakstēm
      if (unread_conversations != ConversationStore.getUnread()) {
        ConversationStore.setUnread(unread_conversations);
        doEmitChange = true;
      }

      if (doEmitChange) {
        ConversationStore.emitChange();
      }
      break;

    case ActionTypes.CONVERSATION_CLICK:
      ConversationStore.setCanSelectConversation(true);
      ConversationStore.setCurrentId(action.conversationID);
      ConversationStore.setRead(action.conversationID);
      ConversationStore.setLoadMore(false);
      ConversationStore.emitChange();
      break;

    case ActionTypes.CONVERSATION_ACTIVE:
      ConversationStore.setRead(action.conversationID);
      ConversationStore.emitChange();
      break;

    case ActionTypes.MESSAGES_LOAD_SUCCESS:
      AppDispatcher.waitFor([MessageStore.dispatchToken]);
      // ja nav sarakstes, tad nepievieno vēstules sarakstei
      // viens no gadījumiem ir profila skatā
      if (!ConversationStore.getTotal()) {
        break;
      }
      var count = action.messages.length;
      var unread_conversations = action.unread_conversations;
      var doEmitChange = false;

      if (count > 0) {
        const memberIds = MessageStore.getMemberIdFromMessages(action.messages);
        ConversationStore.addMessages(memberIds);
        doEmitChange = true;
      }

      // pievieno skaitu ar neizlasītām sarakstēm
      if (unread_conversations != ConversationStore.getUnread()) {
        ConversationStore.setUnread(unread_conversations);
        doEmitChange = true;
      }

      if (doEmitChange) {
        ConversationStore.emitChange();
      }
      break;

    case ActionTypes.MESSAGE_DELETE:
      AppDispatcher.waitFor([MessageStore.dispatchToken]);
      ConversationStore.addMessages([action.message.member_id]);
      ConversationStore.emitChange();
      break;

      // dzēst sarakstes
    case ActionTypes.CONVERSATIONS_DELETE:
      // nav atzīmētās
      if (_.isEmpty(action.conversations_ids)) {
        break;
      }

      // iziet katrai cauri un notiek dzēšana
      _.forEach(action.conversations_ids, (conversation_id) => {
        // dzēst saraksti
        ConversationStore.deleteConversation(conversation_id);
      });

      if (!ConversationStore.getTotal()) {
        ConversationStore.setOpenConversationsCount(false);
      }


      ConversationStore.emitChange();
      break;

      // atzīmēt saraksti
    case ActionTypes.CONVERSATION_CHECKED:
      // status TRUE norāda, ka ieķeksēts (atzīmēts) uz FALSE pretēji
      if (action.status) {
        ConversationStore.setChecked(action.conversationID);
      } else {
        ConversationStore.removeChecked(action.conversationID);
      }

      ConversationStore.emitChange();
      break;

    default:
			// do nothing
  }
});

module.exports = ConversationStore;
